import { SnackbarKey, useSnackbar } from 'notistack';
import React, { FC, PropsWithChildren, createContext } from 'react';
import { MaterialDesignContent } from 'notistack';
import { IconButton, styled } from '@mui/material';
import Icon from 'DesignSystem/Primitives/Icon/Icon';

export const StyledMaterialSnackbar = styled(MaterialDesignContent)(({ theme }) => ({
  '&.notistack-MuiContent': {
    boxShadow: theme.custom.shadows[0],
    borderRadius: 50,
    opacity: 0.8,
    '#notistack-snackbar': {
      maxWidth: '95%', // For multi-line snackbars the icon and close button should be in the same row
    },
  },
  '&.notistack-MuiContent-success': {
    backgroundColor: theme.custom.themeColors.success[100],
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.custom.themeColors.error[100],
  },
}));

export interface IMessageContextValues {
  setErrorMessage: (message: string) => void;
  setSuccessMessage: (message: string) => void;
}

const MessageContext = createContext<IMessageContextValues>({
  setErrorMessage: () => console.warn('Provider not implemented'),
  setSuccessMessage: () => console.warn('Provider not implemented'),
});

const SNACKBAR_AUTO_HIDE_DURATION = 6000;

const MessageProvider: FC<PropsWithChildren> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const action = (snackbarId: SnackbarKey) => (
    <IconButton size="small" onClick={() => closeSnackbar(snackbarId)}>
      <Icon name="cross" size="small" color="white" />
    </IconButton>
  );

  const setErrorMessage = (message: string) => {
    enqueueSnackbar(message || '', {
      variant: 'error',
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      autoHideDuration: SNACKBAR_AUTO_HIDE_DURATION,
      action,
    });
  };

  const setSuccessMessage = (message: string) => {
    enqueueSnackbar(message || '', {
      variant: 'success',
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      autoHideDuration: SNACKBAR_AUTO_HIDE_DURATION,
      action,
    });
  };

  return (
    <MessageContext.Provider
      value={{
        setErrorMessage,
        setSuccessMessage,
      }}
    >
      {children}
    </MessageContext.Provider>
  );
};

export { MessageProvider };

export default MessageContext;
